package com.daytimegaming.ourdailyfinances.presentation.home

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.daytimegaming.ourdailyfinances.domain.Response
import com.daytimegaming.ourdailyfinances.domain.model.RecurringTransactionStream
import com.daytimegaming.ourdailyfinances.domain.usecase.AccountUseCase
import com.daytimegaming.ourdailyfinances.domain.repository.TransactionRepository
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.combine
import kotlinx.coroutines.launch

import com.daytimegaming.ourdailyfinances.data.settings.SettingsManager

enum class RecurringSortType {
    RECENT,
    HIGHEST,
    LOWEST;

    fun next(): RecurringSortType {
        return when (this) {
            RECENT -> HIGHEST
            HIGHEST -> LOWEST
            LOWEST -> RECENT
        }
    }
}

sealed class RecurringTransactionsScreenState {
    data object Loading : RecurringTransactionsScreenState()
    data class Error(val message: String) : RecurringTransactionsScreenState()
    data class Loaded(
        val streams: List<RecurringTransactionStream>,
        val accountNames: Map<String, String> = emptyMap(),
        val sortType: RecurringSortType = RecurringSortType.RECENT
    ) : RecurringTransactionsScreenState()
}

class RecurringTransactionsViewModel(
    private val accountUseCase: AccountUseCase,
    private val transactionRepository: TransactionRepository,
    private val settingsManager: SettingsManager
) : ViewModel() {

    private val _state = MutableStateFlow<RecurringTransactionsScreenState>(RecurringTransactionsScreenState.Loading)
    val state: StateFlow<RecurringTransactionsScreenState> = _state.asStateFlow()

    init {
        load()
    }

    fun load() {
        val savedSortStr = settingsManager.getRecurringTransactionsSort()
        val initialSortType = try {
            RecurringSortType.valueOf(savedSortStr)
        } catch (e: Exception) {
            RecurringSortType.RECENT
        }

        viewModelScope.launch {
            if (_state.value !is RecurringTransactionsScreenState.Loaded) {
                _state.value = RecurringTransactionsScreenState.Loading
            }
            transactionRepository.refreshRecurringTransactions()
        }
        viewModelScope.launch {
            combine(
                transactionRepository.recurringTransactions,
                accountUseCase.GetAccounts(),
                accountUseCase.GetPlaidItems()
            ) { response, accountsResponse, plaidItemsResponse ->
                when {
                    response is Response.Error -> RecurringTransactionsScreenState.Error(response.message)
                    accountsResponse is Response.Error -> RecurringTransactionsScreenState.Error(accountsResponse.message)
                    plaidItemsResponse is Response.Error -> RecurringTransactionsScreenState.Error(plaidItemsResponse.message)
                    response is Response.Loading || accountsResponse is Response.Loading || plaidItemsResponse is Response.Loading -> {
                        if (_state.value is RecurringTransactionsScreenState.Loaded) {
                            _state.value
                        } else {
                            RecurringTransactionsScreenState.Loading
                        }
                    }
                    response is Response.Success && accountsResponse is Response.Success && plaidItemsResponse is Response.Success -> {
                        val items = plaidItemsResponse.data
                        val namesMap = accountsResponse.data.associate { account ->
                            val institutionName = items.find { it.itemId == account.itemId }?.institutionName
                            val fullName = if (!institutionName.isNullOrBlank()) {
                                "${account.displayName} • $institutionName"
                            } else {
                                account.displayName
                            }
                            account.accountId to fullName
                        }
                        val filteredStreams = response.data.filter { stream ->
                            (stream.personalFinanceCategory?.primary != "LOAN_PAYMENTS" ||
                             stream.personalFinanceCategory.detailed == "LOAN_PAYMENTS_MORTGAGE_PAYMENT") &&
                            stream.personalFinanceCategory?.primary != "TRANSFER_OUT"
                        }
                        val currentSort = (_state.value as? RecurringTransactionsScreenState.Loaded)?.sortType ?: initialSortType
                        RecurringTransactionsScreenState.Loaded(filteredStreams, namesMap, currentSort)
                    }
                    else -> _state.value
                }
            }.collect { newState ->
                _state.value = newState
            }
        }
    }

    fun cycleSortType() {
        val currentState = _state.value as? RecurringTransactionsScreenState.Loaded ?: return
        val nextSort = currentState.sortType.next()
        settingsManager.setRecurringTransactionsSort(nextSort.name)
        _state.value = currentState.copy(sortType = nextSort)
    }
}
